import { Component, inject, computed, HostListener, ElementRef, ViewChild, input, output, signal, effect } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { profileStore } from '@core/store';
import { Condominium } from '@core/types';
import { ButtonModule } from 'primeng/button';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { truncateString } from '@shared/utils/';
import { TooltipModule } from 'primeng/tooltip';
import { IconFieldModule } from 'primeng/iconfield';
import { InputTextModule } from 'primeng/inputtext';
import { InputIconModule } from 'primeng/inputicon';
import { Router } from '@angular/router';
import { ADMIN_ROUTE_CONSTANTS } from '@shared/constants/routes';
import { CondominiumQuery } from '@core/query/condominium.query';
import { AppMessageService } from '@shared/services';
import { SkeletonModule } from 'primeng/skeleton';

@Component({
  selector: 'app-condominium-switch',
  standalone: true,
  imports: [CommonModule, FormsModule, ButtonModule, TranslateModule,
    TooltipModule, IconFieldModule, InputTextModule, InputIconModule, SkeletonModule],
  templateUrl: './condominium-switch.component.html',
  styleUrl: './condominium-switch.component.css',
})
export class CondominiumSwitchComponent {
  isCollapsed = input<boolean>(false);
  collapsedChange = output<boolean>();
  searchTerm = signal<string>('');
  isOpen = false;
  condominiumQuery = inject(CondominiumQuery);
  messageService = inject(AppMessageService);

  @ViewChild('dropdownContainer') dropdownContainer!: ElementRef;
  profile = inject(profileStore);
  translate = inject(TranslateService);
  truncateString = truncateString;

  // Usar TanStack Query para obtener condominios
  condominiumsQuery = this.condominiumQuery.useCondominiumSwitch();

  constructor(private elementRef: ElementRef, private router: Router) {
    // Reaccionar a cambios en activeTenantId para invalidar query
    effect(() => {
      const tenantId = this.profile.data.activeTenantId();
      if (tenantId) {
        // El query se invalidará automáticamente porque el queryKey incluye tenantId
        // Cuando los condominios se carguen, establecer el primero como activo si no hay uno
        //this.condominiumsQuery.refetch();
        const condominiums = this.condominiumsQuery.data();
        if (condominiums && condominiums.length > 0 && !this.profile.data.activeCondominiumId()) {
          this.profile.setActiveCondominium(condominiums[0].id);
        }
      }
    });

    // Reaccionar a cambios en los datos del query para actualizar el store
    effect(() => {
      const condominiums = this.condominiumsQuery.data();
      const isLoading = this.condominiumsQuery.isLoading();
      if (condominiums !== undefined && !isLoading) {
        this.profile.setCondominiums(condominiums);

        if (condominiums.length === 0) {
          // No hay condominios: redirigir a creación si no está ya ahí
          const createPath = `/${ADMIN_ROUTE_CONSTANTS.path}/${ADMIN_ROUTE_CONSTANTS.condominium.create.path}`;
          if (this.router.url !== createPath && !this.router.url.startsWith(createPath)) {
            this.router.navigate([createPath]);
          }
        } else {
          // Hay condominios: si no hay activo, establecer el primero
          if (!this.profile.data.activeCondominiumId()) {
            this.profile.setActiveCondominium(condominiums[0].id);
          }

          // Si estamos en la página de creación y el guard nos envió ahí por precaución,
          // redirigir al dashboard
          const createPath = `/${ADMIN_ROUTE_CONSTANTS.path}/${ADMIN_ROUTE_CONSTANTS.condominium.create.path}`;
          if (this.router.url === createPath || this.router.url.startsWith(createPath)) {
            this.router.navigate(['/', ADMIN_ROUTE_CONSTANTS.path, ...ADMIN_ROUTE_CONSTANTS.dashboard.path.split('/')]);
          }
        }
      }
    });
  }

  condominiums = computed(() => {
    // Usar datos del query en lugar del store directamente
    return this.condominiumsQuery.data() || [];
  });

  filteredCondominiums = computed(() => {
    const condos = this.condominiums();
    const search = this.searchTerm().toLowerCase().trim();

    if (!search) {
      return condos;
    }

    return condos.filter(condo =>
      condo.name.toLowerCase().includes(search) ||
      condo.email.toLowerCase().includes(search)
    );
  });

  selectedCondominium = computed(() => {
    const activeId = this.profile.data.activeCondominiumId();
    const condos = this.condominiums();
    // Buscar el condominio con el activeId, si no existe usar el primero como fallback
    const found = condos.find(c => c.id === activeId);
    return found ?? (condos.length > 0 ? condos[0] : null);
  });



  toggleDropdown(event: Event): void {
    event.stopPropagation();
    this.isOpen = !this.isOpen;
    if (this.isOpen && this.isCollapsed()) {
      this.toggleSidebarCollapse();
    }
    // Reset search when closing dropdown
    if (!this.isOpen) {
      this.searchTerm.set('');
    }
  }

  selectCondominium(condominium: Condominium): void {
    this.profile.setActiveCondominium(condominium.id);
    this.isOpen = false;
  }

  getInitial(name: string): string {
    return name ? name.charAt(0).toUpperCase() : '';
  }

  toggleSidebarCollapse(): void {
    this.collapsedChange.emit(!this.isCollapsed());
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.isOpen = false;
      // Reset search when closing dropdown
      this.searchTerm.set('');
    }
  }

  navigateToCondominiumCreate(): void {
    this.router.navigate(['/', ADMIN_ROUTE_CONSTANTS.path, ...ADMIN_ROUTE_CONSTANTS.condominium.create.path.split('/')]);
  }

  refreshCondominiums(): void {
    this.isOpen = false;
    // Invalidar el query para forzar recarga
    this.condominiumsQuery.refetch();
  }

  navigateToCondominiumEdit(event?: unknown): void {
    // Evitar que el click dispare toggleDropdown (click del contenedor padre)
    const anyEvent = event as any;
    anyEvent?.stopPropagation?.();
    anyEvent?.originalEvent?.stopPropagation?.();

    // Cerrar dropdown si está abierto
    this.isOpen = false;
    this.searchTerm.set('');

    this.router.navigate(['/', ADMIN_ROUTE_CONSTANTS.path, ...ADMIN_ROUTE_CONSTANTS.condominium.edit.path.split('/')]);
  }

  get isLoading(): boolean {
    return this.condominiumsQuery.isLoading();
  }
}
